import React, { useMemo, useRef, useState } from 'react';
import Back from '../../features/back/Back';
import useStateControls from '../../../hooks/useStateControls';
import { safeHTML } from '../../../utils/safeHTML';
import classNames from 'classnames';
import { useModal } from '../../../hooks/useModal';
import { useContent } from '../../../redux/reducer/content';
import { ProcedureDragArea } from './ProcedureDragArea';
import Room from '../../features/room/Room';
import { getEventPosition } from '../../../utils/scene/utils/events/convert';
import { useRequests } from '../../../redux/reducer/requests';
import { useRequestsHandlerData } from '../../../hooks/useRequestsHandlerData';
import AppLoader from '../../features/appLoader/AppLoader';
import { CSSTransition } from 'react-transition-group';
import { useElf } from '../../../redux/reducer/elf';
import { procedureGameResultContent } from '../../../constants/copyright-modals';
import { randomChoice } from '../../../utils/helpers/helpers';
import CustomButton from '../../features/customButton/ui/main/CustomButton';
import { soundManager } from '../../../utils/scene/sound/SoundManager';
import { SOUND_DELAY } from '../../../constants/components/scenes/elf/settings';
import { useTranslation } from '../../../translations';

const ProcedureGame = React.forwardRef(function ProcedureGame({ items, description, button, callbacks }, ref) {
  const {
    authData: { isWeb },
  } = useRequests();
  const { addModal } = useModal();
  const { setState } = useStateControls();
  const { beautyRoutine } = useContent();
  const { isAnimation } = useElf();
  const [activeRoutine, setActiveRoutine] = useState();
  const [activeArea, setActiveArea] = useState();
  const [lastActive, setLastActive] = useState();
  const [routinesPool, setRoutinesPool] = useState([]);
  const [resultScore, setResultScore] = useState(0);
  const [userPrize, setUserPrize] = useState();
  const routineAreas = useRef([]);
  const { t } = useTranslation();

  const goBack = () => setState('main');

  const setActiveRoutineCallback = (e, el) => {
    const { x, y } = getEventPosition(e);
    setActiveRoutine({ ...el, position: { x, y } });
  };

  const [result, setResult] = useState([]);

  const checkResult = () => {
    callbacks.endBeautyRoutine({
      id: beautyRoutine?.id,
      sessionId: beautyRoutine?.sessionId,
      placement: routinesPool.map((card) => card?.id),
    });
  };

  const goBeautyAnimation = () => {
    callbacks.addAnimations([{ state: 'procedureGame', animation: 'beforeBeauty' }]);
    setTimeout(() => soundManager.onPlay('beforeBeauty'), SOUND_DELAY.beforeBeauty);
    callbacks.addOutsideAnimations([{ state: 'main', animation: randomChoice('wink', 'happy') }]);
  };

  const openResultModal = () => {
    addModal({
      type: 'procedureGameResult',
      props: {
        ...procedureGameResultContent,
        text: beautyRoutine[`${result?.every(({ isRight }) => isRight) ? 'correct' : 'wrong'}_description`],
        score: resultScore,
        items: result,
        userPrize,
        callbacks,
      },
    });
  };

  const shuffledItems = beautyRoutine?.cards ?? [];

  const isAllDelivered = useMemo(() => {
    return routinesPool?.reduce((acc, el) => acc + Boolean(el), 0) === beautyRoutine?.slots?.length;
  }, [routinesPool, beautyRoutine]);

  const isPending = useRequestsHandlerData([
    {
      address: 'requestsEndBeautyRoutine',
      fulfilled: ({ userRoutine, prize, placementResult }) => {
        setResult(placementResult);
        setUserPrize(prize);
        setResultScore(userRoutine.score);
        callbacks.setIsAnimation(true);
      },
      rejected: goBack,
    },
  ]);

  return (
    <div className={classNames('procedure-game', { app: !isWeb })} ref={ref}>
      <div className={`procedure-game__header`}>
        <Back onClick={goBack} className={'back_black'} />
        <h1 className={'procedure-game__title'}>{safeHTML(beautyRoutine?.title)}</h1>
      </div>
      <div className={'procedure-game__content'}>
        <p className={'procedure-game__note'}>{safeHTML(t(description))}</p>
        <div className={'procedure-game__activities'}>
          {beautyRoutine?.slots?.map((el, id) => {
            const { action } = el;
            const deliveredItem = routinesPool[id];
            const startDrag = (e) => !!deliveredItem && setActiveRoutineCallback(e, deliveredItem);
            const isHaveImg = !!deliveredItem && activeRoutine?.id !== deliveredItem?.id;
            return (
              <div
                ref={(ref) => (routineAreas.current[id] = { ref, el, index: id })}
                key={`act${id}`}
                className={classNames(`procedure-game__activity procedure-game__activity_${id}`, {
                  'procedure-game__activity_active': activeArea?.id === el?.id || deliveredItem,
                })}
              >
                <div className={'procedure-game__activity-icon'}>
                  <div className={'procedure-game__activity-num'}>{id + 1}</div>
                  <div className={'procedure-game__activity-icon-img'} onTouchStart={startDrag} onMouseDown={startDrag}>
                    {isHaveImg && <img src={deliveredItem.image} alt={''} />}
                  </div>
                </div>
                <div className={'procedure-game__activity-name'}>{safeHTML(action)}</div>
              </div>
            );
          })}
        </div>
        {(lastActive || activeRoutine) && !isAllDelivered && (
          <div className={'procedure-game__item'}>
            <h3 className={'procedure-game__item-name'}>{safeHTML(activeRoutine?.brand ?? lastActive?.brand)}</h3>
            <p className={'procedure-game__item-description'}>
              {safeHTML(activeRoutine?.description ?? lastActive?.description)}
            </p>
          </div>
        )}
        <div className={'procedure-game__elems'}>
          {shuffledItems.map((el, id) => {
            const { image } = el;
            const isHaveDeliveredItem = routinesPool?.some((routine) => routine?.id === el?.id);
            const startDrag = (e) => !isHaveDeliveredItem && setActiveRoutineCallback(e, el);
            return (
              <div
                key={`el${id}`}
                className={classNames('procedure-game__el', {
                  'procedure-game__el_active': activeRoutine?.id === el?.id,
                  'procedure-game__el_complete': isHaveDeliveredItem,
                })}
                onTouchStart={startDrag}
                onMouseDown={startDrag}
              >
                <div className={'procedure-game__el-image-box'}>
                  <div className={'procedure-game__el-image'}>
                    <img src={image} alt={'beauty-routine'} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <ProcedureDragArea
          setLastActive={setLastActive}
          activeRoutine={activeRoutine}
          setActiveRoutine={setActiveRoutine}
          setActiveArea={setActiveArea}
          routineAreas={routineAreas}
          setRoutinesPool={setRoutinesPool}
        />
        {isAllDelivered && (
          <CustomButton {...button.attr} timeout={5000} coins={true} count={beautyRoutine.cost} onClick={checkResult} />
        )}
        <CSSTransition
          in={isAnimation}
          timeout={700}
          classNames={'procedure-game__room'}
          onEntered={goBeautyAnimation}
          onExited={openResultModal}
          mountOnEnter={true}
          unmountOnExit={true}
        >
          <div className={'procedure-game__room'}>
            <Room callbacks={callbacks} />
          </div>
        </CSSTransition>
        {isPending && <AppLoader />}
      </div>
    </div>
  );
});
export default ProcedureGame;
ProcedureGame.propTypes = {};
